import React, { useState } from "react";

import {
  Box,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

/**
 * Temperature Correction Factor (TCF) reference tables for liquefied gas
 * transloading (LPG and similar products).
 *
 * Factors convert an observed liquid volume at the tank/meter temperature to
 * the equivalent volume at the 60°F (API) reference temperature:
 *
 *     volume@60°F = observed volume × TCF
 *
 * Liquefied gases have high thermal expansion, so the correction is large
 * relative to crude/refined products — hence dedicated tables per product.
 *
 * NOTE: Demo data only. Values are representative and hard-coded for display;
 * they are NOT certified API/GPA tables and are not persisted or used in any
 * volume/billing calculation.
 */
const TCF_TABLES = [
  {
    id: "propane",
    label: "Commercial Propane (HD-5)",
    relativeDensity: 0.504,
    rows: [
      { tempF: -20, factor: 1.124 },
      { tempF: -10, factor: 1.107 },
      { tempF: 0, factor: 1.09 },
      { tempF: 10, factor: 1.073 },
      { tempF: 20, factor: 1.057 },
      { tempF: 30, factor: 1.041 },
      { tempF: 40, factor: 1.026 },
      { tempF: 50, factor: 1.013 },
      { tempF: 60, factor: 1.0 },
      { tempF: 70, factor: 0.984 },
      { tempF: 80, factor: 0.967 },
      { tempF: 90, factor: 0.95 },
      { tempF: 100, factor: 0.933 },
      { tempF: 110, factor: 0.915 },
      { tempF: 120, factor: 0.897 },
    ],
  },
  {
    id: "nButane",
    label: "Commercial Butane (n-Butane)",
    relativeDensity: 0.584,
    rows: [
      { tempF: -20, factor: 1.078 },
      { tempF: -10, factor: 1.068 },
      { tempF: 0, factor: 1.058 },
      { tempF: 10, factor: 1.047 },
      { tempF: 20, factor: 1.037 },
      { tempF: 30, factor: 1.027 },
      { tempF: 40, factor: 1.018 },
      { tempF: 50, factor: 1.009 },
      { tempF: 60, factor: 1.0 },
      { tempF: 70, factor: 0.99 },
      { tempF: 80, factor: 0.981 },
      { tempF: 90, factor: 0.971 },
      { tempF: 100, factor: 0.962 },
      { tempF: 110, factor: 0.952 },
      { tempF: 120, factor: 0.943 },
    ],
  },
  {
    id: "isobutane",
    label: "Isobutane",
    relativeDensity: 0.563,
    rows: [
      { tempF: -20, factor: 1.092 },
      { tempF: -10, factor: 1.08 },
      { tempF: 0, factor: 1.069 },
      { tempF: 10, factor: 1.057 },
      { tempF: 20, factor: 1.045 },
      { tempF: 30, factor: 1.034 },
      { tempF: 40, factor: 1.022 },
      { tempF: 50, factor: 1.011 },
      { tempF: 60, factor: 1.0 },
      { tempF: 70, factor: 0.988 },
      { tempF: 80, factor: 0.977 },
      { tempF: 90, factor: 0.965 },
      { tempF: 100, factor: 0.954 },
      { tempF: 110, factor: 0.942 },
      { tempF: 120, factor: 0.93 },
    ],
  },
  {
    id: "propaneButane5050",
    label: "Propane / Butane Mix (50/50)",
    relativeDensity: 0.544,
    rows: [
      { tempF: -20, factor: 1.101 },
      { tempF: -10, factor: 1.088 },
      { tempF: 0, factor: 1.074 },
      { tempF: 10, factor: 1.06 },
      { tempF: 20, factor: 1.047 },
      { tempF: 30, factor: 1.034 },
      { tempF: 40, factor: 1.022 },
      { tempF: 50, factor: 1.011 },
      { tempF: 60, factor: 1.0 },
      { tempF: 70, factor: 0.987 },
      { tempF: 80, factor: 0.974 },
      { tempF: 90, factor: 0.958 },
      { tempF: 100, factor: 0.944 },
      { tempF: 110, factor: 0.929 },
      { tempF: 120, factor: 0.914 },
    ],
  },
  {
    id: "ammonia",
    label: "Anhydrous Ammonia (NH₃)",
    relativeDensity: 0.682,
    rows: [
      { tempF: -20, factor: 1.064 },
      { tempF: -10, factor: 1.055 },
      { tempF: 0, factor: 1.046 },
      { tempF: 10, factor: 1.038 },
      { tempF: 20, factor: 1.029 },
      { tempF: 30, factor: 1.021 },
      { tempF: 40, factor: 1.014 },
      { tempF: 50, factor: 1.007 },
      { tempF: 60, factor: 1.0 },
      { tempF: 70, factor: 0.992 },
      { tempF: 80, factor: 0.985 },
      { tempF: 90, factor: 0.977 },
      { tempF: 100, factor: 0.969 },
      { tempF: 110, factor: 0.961 },
      { tempF: 120, factor: 0.953 },
    ],
  },
];

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  intro: {
    marginBottom: theme.spacing(2),
  },
  formula: {
    fontFamily: "monospace",
    backgroundColor: theme.palette.action.hover,
    padding: theme.spacing(0.5, 1),
    borderRadius: 4,
  },
  selector: {
    minWidth: 320,
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    maxWidth: 520,
  },
  refRow: {
    backgroundColor: theme.palette.action.selected,
  },
  factorCell: {
    fontVariantNumeric: "tabular-nums",
    fontFamily: "monospace",
  },
  meta: {
    marginTop: theme.spacing(1.5),
  },
}));

export const TCFTablesForm = () => {
  const classes = useStyles();
  const [selectedId, setSelectedId] = useState(TCF_TABLES[0].id);

  const table = TCF_TABLES.find((t) => t.id === selectedId) || TCF_TABLES[0];

  return (
    <Box className={classes.root}>
      <Box className={classes.intro}>
        <Typography variant="h6" gutterBottom>
          Temperature Correction Factors (TCF)
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          For liquefied gas transloading (LPG and similar). Factors correct an
          observed liquid volume at the measured temperature to the equivalent
          volume at the 60°F reference temperature:
        </Typography>
        <Typography variant="body2" component="div" gutterBottom>
          <span className={classes.formula}>
            Volume @ 60°F = Observed Volume × TCF
          </span>
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Reference / display values only — not certified API·GPA tables, and
          not used in any volume or billing calculation.
        </Typography>
      </Box>

      <TextField
        select
        variant="outlined"
        label="Product Table"
        value={selectedId}
        onChange={(e) => setSelectedId(e.target.value)}
        className={classes.selector}
      >
        {TCF_TABLES.map((t) => (
          <MenuItem key={t.id} value={t.id}>
            {t.label}
          </MenuItem>
        ))}
      </TextField>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table size="small" aria-label={`${table.label} TCF table`}>
          <TableHead>
            <TableRow>
              <TableCell>Temperature (°F)</TableCell>
              <TableCell align="right">Correction Factor</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {table.rows.map((row) => (
              <TableRow
                key={row.tempF}
                className={row.tempF === 60 ? classes.refRow : undefined}
              >
                <TableCell>
                  {row.tempF}
                  {row.tempF === 60 ? " (reference)" : ""}
                </TableCell>
                <TableCell align="right" className={classes.factorCell}>
                  {row.factor.toFixed(3)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography
        variant="caption"
        color="textSecondary"
        component="div"
        className={classes.meta}
      >
        {table.label} — relative density ≈ {table.relativeDensity.toFixed(3)} @
        60°F.
      </Typography>
    </Box>
  );
};
